/**
 * Crops a face from an image file based on a normalized bounding box.
 * @param {File} file The original image file
 * @param {Array<number>} bbox [ymin, xmin, ymax, xmax] in 0-1000 range
 * @returns {Promise<Blob>} The cropped image blob
 */
export const cropImageToBlob = (file, bbox) => {
  return new Promise((resolve, reject) => {
    if (!file || !bbox || bbox.length !== 4) {
      return reject(new Error("Invalid file or bounding box"));
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      
      const [ymin, xmin, ymax, xmax] = bbox;
      
      // Calculate pixel coordinates
      const pxMinY = (ymin / 1000) * img.height;
      const pxMinX = (xmin / 1000) * img.width;
      const pxMaxY = (ymax / 1000) * img.height;
      const pxMaxX = (xmax / 1000) * img.width;
      
      const width = pxMaxX - pxMinX;
      const height = pxMaxY - pxMinY;
      
      const cx = pxMinX + width / 2;
      const cy = pxMinY + height / 2;
      
      // Zoom in significantly (0.75) to cut out the chest/shoulders and focus on the face.
      const size = Math.max(width, height);
      const paddedSize = size * 0.75;
      
      // Shift the center slightly up (10%) because the face is usually in the upper half of the photo box.
      const adjustedCy = cy - (height * 0.1);
      
      let cropX = cx - (paddedSize / 2);
      let cropY = adjustedCy - (paddedSize / 2);
      let cropW = paddedSize;
      let cropH = paddedSize;

      // Create a square canvas
      const canvas = document.createElement('canvas');
      canvas.width = paddedSize;
      canvas.height = paddedSize;
      const ctx = canvas.getContext('2d');
      
      // Fill with a background color in case the crop goes out of bounds
      ctx.fillStyle = "#f8fafc";
      ctx.fillRect(0, 0, paddedSize, paddedSize);
      
      let sx = cropX;
      let sy = cropY;
      let sWidth = cropW;
      let sHeight = cropH;
      let dx = 0;
      let dy = 0;
      let dWidth = cropW;
      let dHeight = cropH;

      if (sx < 0) {
        dx = -sx;
        sWidth += sx;
        dWidth += sx;
        sx = 0;
      }
      if (sy < 0) {
        dy = -sy;
        sHeight += sy;
        dHeight += sy;
        sy = 0;
      }
      if (sx + sWidth > img.width) {
        const excess = (sx + sWidth) - img.width;
        sWidth -= excess;
        dWidth -= excess;
      }
      if (sy + sHeight > img.height) {
        const excess = (sy + sHeight) - img.height;
        sHeight -= excess;
        dHeight -= excess;
      }

      ctx.drawImage(img, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
      
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Canvas toBlob failed"));
        }
      }, 'image/jpeg', 0.9);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
};
